import { Button, Card, Col, Collapse, Row, Spin, Tabs } from 'antd';
import { BulbOutlined, LoadingOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import { getI18n } from '../../../utils/Utils';
import { useNavigate } from 'react-router-dom';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { IAppComponentProps } from '../../../components';
import EimDashboardSearchForm from './components/eimDashboardSearchForm';
import EimDashboardStatsCard from './components/eimDashboardStatsCard';
import {
    useAllActivePartnersLazyQuery,
    useCountAllActivePartnersLazyQuery,
    useDownloadAllActivePartnersLazyQuery,
    useGetDashCardCountLazyQuery,
} from './dashboardQueries.generated';
import ReactEcharts from 'echarts-for-react';
import dayjs from 'dayjs';
import { getDashboardCardCountData } from './eimDashboardUtils';
import { getActivePartnerGroupBarChartOptions, getAllActiveInvoiceGraphOptions } from './utils/InvoiceStatusUtils';
import { EimDashboardRequest } from '../../../__generated__/types';
import { getDateFormatOrDefault } from '../../users/UserFormatPreferenceServices';
import monitoringStyles from './eimMonitoringDashboard.module.scss';
import { EIM_THEME_STORAGE_KEY } from '../themePreference';

type ThemeMode = 'dark' | 'light';

const EIMDashboard: FunctionComponent<IAppComponentProps> = (props) => {
    const [loading, isLoading] = useState(false);
    const [theme, setTheme] = useState<ThemeMode>(() => {
        const storedTheme = globalThis.localStorage.getItem(EIM_THEME_STORAGE_KEY);
        return storedTheme === 'light' ? 'light' : 'dark';
    });
    const intl = useIntl();
    const navigate = useNavigate();

    const [fromDate, setFromDate] = useState(undefined);
    const [toDate, setToDate] = useState(undefined);
    const [company, setCompany] = useState(undefined);
    const [process, setProcess] = useState(undefined);
    const [companyGroup, setCompanyGroup] = useState(undefined);

    const [activePartnerDataGraphOption, setActivePartnerDataGraphOption] = useState(undefined);
    const [graphData, setGraphData] = useState(undefined);
    const [rawGroupCountData, setRawGroupCountData] = useState<any[]>(undefined);
    const [rawAllActivePartnerData, setRawAllActivePartnerData] = useState<any[]>(undefined);

    const [getAllActivePartnerGraphData, {}] = useCountAllActivePartnersLazyQuery();
    const [getAllActivePartners, {}] = useAllActivePartnersLazyQuery();
    const [downloadAllActivePartners, {}] = useDownloadAllActivePartnersLazyQuery();
    const [getDashCardCount, _] = useGetDashCardCountLazyQuery();
    const [statsCardDataList, setStatsCardDataList] = useState(undefined);

    const redirectToMain = () => {
        const effectiveFromDate = fromDate && dayjs(fromDate).isValid() ? fromDate : dayjs();
        const effectiveToDate = toDate && dayjs(toDate).isValid() ? toDate : dayjs();

        globalThis.sessionStorage.setItem('fromDate', effectiveFromDate.format('YYYY-MM-DD'));
        globalThis.sessionStorage.setItem('toDate', effectiveToDate.format('YYYY-MM-DD'));
        if (company !== undefined) {
            globalThis.sessionStorage.setItem('company', JSON.stringify(company));
        }
        if (process !== undefined) {
            globalThis.sessionStorage.setItem('process', JSON.stringify(process));
        }
        if (companyGroup !== undefined) {
            globalThis.sessionStorage.setItem('companyGroup', JSON.stringify(companyGroup));
        }
        navigate('../eim-document-overview-monitoring');
    };

    const getAllActivePartnerGraphDataOption = async () => {
        getAllActivePartnerGraphData().then((response) => {
            const raw = response?.data?.countAllActivePartnerData.groupCountData;
            setRawGroupCountData(raw);
            const graphOption = getActivePartnerGroupBarChartOptions(raw, theme);
            setActivePartnerDataGraphOption(graphOption);
        });
    };
    const getAllActivePartnersGraphData = async (request: EimDashboardRequest) => {
        getAllActivePartners({ variables: { request } }).then((response) => {
            const raw = response.data.allActivePartnerData;
            setRawAllActivePartnerData(raw);
            const graphOption = getAllActiveInvoiceGraphOptions(raw, theme);
            setGraphData(graphOption);
        });
    };

    const getDashboardCardData = async (req: EimDashboardRequest) => {
        isLoading(true);
        getDashCardCount({
            variables: {
                request: {
                    fromDate: req && req.fromDate ? req.fromDate : dayjs(),
                    toDate: req && req.toDate ? req.toDate : dayjs(),
                },
            },
        }).then((response) => {
            if (response && response.data) {
                setStatsCardDataList(getDashboardCardCountData(response.data.getDashCardCount));
            }
            isLoading(false);
        });
    };

    const handleSearch = (values) => {
        setFromDate(values.fromDate);
        setToDate(values.toDate);
        setCompany(values.company);
        setProcess(values.process);
        setCompanyGroup(values.companyGroup);

        const req: EimDashboardRequest = {
            fromDate: values.fromDate ? dayjs(values.fromDate).endOf("date") : undefined,
            toDate: values.toDate ? dayjs(values.toDate).endOf("date") : undefined,
            company: values.company,
            process: values.process,
            companyGroup: values.companyGroup,
        };
        getDashboardCardData(req).catch(console.error);
        getAllActivePartnersGraphData(req).catch(console.error);
        getAllActivePartnerGraphDataOption().catch(console.error);
    };

    const handleReset = () => {
        const req: EimDashboardRequest = {};
        getDashboardCardData(req).catch(console.error);
        getAllActivePartnersGraphData(req).catch(console.error);
        getAllActivePartnerGraphDataOption().catch(console.error);
    };
    useEffect(() => {
        if (!fromDate && !toDate) {
            const req: EimDashboardRequest = {};
            getDashboardCardData(req).catch(console.error);
            getAllActivePartnersGraphData(req).catch(console.error);
            getAllActivePartnerGraphDataOption().catch(console.error);
        }
    }, [fromDate, toDate, company, process, companyGroup]);

    useEffect(() => {
        globalThis.localStorage.setItem(EIM_THEME_STORAGE_KEY, theme);
        if (rawGroupCountData) {
            setActivePartnerDataGraphOption(getActivePartnerGroupBarChartOptions(rawGroupCountData, theme));
        }
        if (rawAllActivePartnerData) {
            setGraphData(getAllActiveInvoiceGraphOptions(rawAllActivePartnerData, theme));
        }
    }, [theme]);

    const title = 'Active BMW Partners by BMW Group as at '.concat(dayjs().format(getDateFormatOrDefault(props.currentUser)));

    return (
        <Spin spinning={loading} size={'large'} indicator={<LoadingOutlined style={{ fontSize: 50 }} />}>
            <div
                className={classNames(monitoringStyles.dashboardPage, 'ag-theme-balham', {
                    [monitoringStyles.dashboardPageLight]: theme === 'light',
                    [monitoringStyles.dashboardPageDark]: theme === 'dark',
                })}
            >
                <div className={monitoringStyles.toolbar}>
                    <Button
                        className={monitoringStyles.themeToggle}
                        icon={<BulbOutlined />}
                        onClick={() => setTheme((previousTheme) => (previousTheme === 'dark' ? 'light' : 'dark'))}
                    >
                        {theme === 'dark' ? 'Switch to light view' : 'Switch to dark view'}
                    </Button>
                </div>

                <Tabs size={'small'} type={'card'}>
                    <Tabs.TabPane tab={intl.formatMessage({ id: 'titlePartnerView' })} key="search">
                        <Collapse className={monitoringStyles.filtersPanel}>
                            <Collapse.Panel
                                key="document-monitoring-description"
                                header={getI18n('search-filters','Search Filters', intl)}
                            >
                                <div className="ag-theme-balham">
                                    <EimDashboardSearchForm
                                        intl={intl}
                                        onSearch={handleSearch}
                                        onReset={handleReset}
                                        currentUser={props.currentUser} />
                                </div>
                            </Collapse.Panel>
                            </Collapse>

                        <Row gutter={20} className={monitoringStyles.sectionSpacing}>
                            <Col span={24}>
                                <Card bordered={true} size={'small'} onClick={() => redirectToMain()} className={monitoringStyles.summaryCard}>
                                    <Row gutter={20}>
                                        {statsCardDataList &&
                                            statsCardDataList.map((data, index) => (
                                                <Col span={3} key={index}>
                                                    <EimDashboardStatsCard
                                                        data={data}
                                                        icon={data.icon}
                                                        style={{ fontSize: '30px', color: data.color }}
                                                        darkMode={theme === 'dark'}
                                                    />
                                                </Col>
                                            ))}
                                    </Row>
                                </Card>
                            </Col>
                        </Row>

                        <Row gutter={20} className={monitoringStyles.sectionSpacing}>
                            <Col span={24}>
                                {activePartnerDataGraphOption && (
                                    <Card
                                        size={'small'}
                                        title={title}
                                        extra="Total could include duplicates if a Partner does AP and AR"
                                        className={monitoringStyles.chartCard}
                                    >
                                        <Row gutter={16}>
                                            <Col span={24}>
                                                {activePartnerDataGraphOption && (
                                                    <ReactEcharts
                                                        option={activePartnerDataGraphOption}
                                                        style={{ height: 500 }}
                                                    />
                                                )}
                                            </Col>
                                        </Row>
                                    </Card>
                                )}
                            </Col>
                        </Row>

                        <Row gutter={10} className={monitoringStyles.sectionSpacing}>
                            <Col span={24}>
                                <Card size={'small'} title="Monthly Active Partners" className={monitoringStyles.chartCard}>
                                    <Row gutter={10}>
                                        <Col span={24}>
                                            {graphData && <ReactEcharts option={graphData} style={{ height: 500 }} />}
                                        </Col>
                                    </Row>
                                </Card>
                            </Col>
                        </Row>
                    </Tabs.TabPane>
                </Tabs>
            </div>
        </Spin>
    );
};
export default EIMDashboard;
